import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as Icons from 'lucide-react';
import { REASONS } from '../mock';
import SectionHead from './SectionHead';
import { EASE } from './reveal';

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      className="relative py-16 sm:py-20 lg:py-32 bg-[#050608] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-20 -left-20 w-[320px] sm:w-[480px] h-[320px] sm:h-[480px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-10 w-[360px] sm:w-[520px] h-[360px] sm:h-[520px] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="Why Choose Us"
          titleLines={[<>The <span key="accent" className="text-cyan-300">Absolute</span> Difference</>]}
          body="We are marine contractors currently serving the southwest Florida market. We build our barges to meet all the needs and demands of a modern marine contracting company while accounting for all the issues of older barges. We use the best materials, modern design and manufacturing practices, and make everything easy to service if needed. We don't cut corners because we actually use our barges every day."
        />

        <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {REASONS.map((r, idx) => {
            const Icon = Icons[r.icon] || Icons.Star;
            return (
              <motion.div
                key={r.title}
                className="panel rounded-xl p-6 sm:p-7"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: EASE, delay: idx * 0.07 }}
              >
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 mb-4 sm:mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-white text-base sm:text-lg font-semibold">{r.title}</h3>
                <p className="mt-2 sm:mt-2.5 text-zinc-400 text-sm leading-relaxed">{r.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Cinematic barge image divider — full-bleed atmospheric shot with slow
 * scroll parallax and feathered top/bottom gradient fades (no hard borders).
 */
export function BargeDivider() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-7%', '7%']);

  return (
    <section
      ref={ref}
      aria-hidden="true"
      className="relative w-full overflow-hidden bg-[#050608] h-[40vh] sm:h-[55vh] md:h-[70vh]"
    >
      {/* Parallax image — 118% height so movement never reveals edges. */}
      <motion.div
        style={{
          y,
          top: '-9%',
          bottom: '-9%',
        }}
        className="atmospheric-parallax absolute inset-x-0 bg-center bg-cover"
      />

      {/* Feathered top fade */}
      <div className="absolute inset-x-0 top-0 h-24 sm:h-36 bg-gradient-to-b from-black via-black/60 to-transparent pointer-events-none" />
      {/* Feathered bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-24 sm:h-36 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
    </section>
  );
}
